import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import useBeerDuplicateReviews from './useBeerDuplicateReviews';
import useBeerDuplicateReviewActions from './useBeerDuplicateReviewActions';
import {
  buildBeerDuplicateInboxPath,
  buildBeerDuplicateReviewPath,
  nextQueuedReviewIdAfter,
  queueReviewIdsFromRows,
  resolveQueueNeighbors,
} from '../utils/beer/beerDuplicateReviewQueue';
import { mergeReviewRowWithMatchedBeer } from '../utils/beer/mapBeerDuplicateReviewToCardBeer';
import {
  captureDuplicateReviewApply,
  captureDuplicateReviewAddBeer,
  captureDuplicateReviewAutoAdvance,
  captureDuplicateReviewComplete,
} from '../utils/engageDuplicateReviewAnalytics';

async function listPendingReviews({ breweryId, globalScope }) {
  const listFn = httpsCallable(
    functions,
    'callableBeerListDuplicateReviews-listDuplicateReviews',
  );
  const payload = globalScope
    ? { status: 'pending' }
    : { breweryId, status: 'pending' };
  const result = await listFn(payload);
  const data = result.data ?? {};
  const rawReviews = Array.isArray(data.reviews) ? data.reviews : [];
  return rawReviews.map((item) => ({
    ...item,
    review_outcome: item.reviewOutcome ?? item.review_outcome,
  }));
}

export default function useBeerDuplicateReviewSession({
  reviewId,
  breweryId,
  globalScope = false,
  initialQueueReviewIds,
  onClose,
}) {
  const navigate = useNavigate();
  const scopedBreweryId = globalScope ? null : breweryId;
  const { reviews, loading, error, refresh } = useBeerDuplicateReviews(
    scopedBreweryId,
    'pending',
    { globalScope },
  );
  const [queueReviewIds, setQueueReviewIds] = useState(
    () => (Array.isArray(initialQueueReviewIds) ? initialQueueReviewIds : []),
  );
  const [activeReview, setActiveReview] = useState(null);
  const {
    completeReview,
    addBeerAndCompleteReview,
    applySelectedChanges,
    loading: actionsLoading,
    errorMessage: actionsError,
    clearError,
  } = useBeerDuplicateReviewActions();

  useEffect(() => {
    setQueueReviewIds(queueReviewIdsFromRows(reviews));
  }, [reviews]);

  useEffect(() => {
    if (!reviewId) {
      setActiveReview(null);
      return;
    }
    if (reviews.length === 0) {
      setActiveReview(null);
      return;
    }
    const matched = reviews.find((row) => row.id === reviewId);
    setActiveReview(matched ?? null);
  }, [reviewId, reviews]);

  const neighbors = useMemo(
    () => resolveQueueNeighbors(reviewId, queueReviewIds),
    [queueReviewIds, reviewId],
  );

  const showQueueNav = queueReviewIds.length > 0;

  const navigateToReview = useCallback((nextReviewId) => {
    if (globalScope) {
      navigate(buildBeerDuplicateReviewPath(nextReviewId), {
        state: { queueReviewIds },
      });
      return;
    }
    if (onClose) {
      onClose(nextReviewId);
    }
  }, [globalScope, navigate, onClose, queueReviewIds]);

  const advanceAfterTerminalAction = useCallback(async () => {
    captureDuplicateReviewAutoAdvance(reviewId, activeReview?.breweryId);
    const previousIds = queueReviewIds;
    const refreshedRows = await listPendingReviews({ breweryId, globalScope });
    const refreshedIds = queueReviewIdsFromRows(refreshedRows);
    setQueueReviewIds(refreshedIds);
    const nextReviewId = nextQueuedReviewIdAfter(reviewId, previousIds, refreshedIds);
    if (!nextReviewId) {
      if (globalScope) {
        navigate(buildBeerDuplicateInboxPath());
      } else if (onClose) {
        onClose(null);
      }
      return;
    }
    navigateToReview(nextReviewId);
  }, [
    activeReview?.breweryId,
    breweryId,
    globalScope,
    navigate,
    navigateToReview,
    onClose,
    queueReviewIds,
    reviewId,
  ]);

  const handleApplySelected = useCallback(async (selectedFields) => {
    if (!activeReview) {
      return { success: false, error: 'Review not loaded' };
    }
    captureDuplicateReviewApply(reviewId, activeReview.breweryId, selectedFields);
    const result = await applySelectedChanges(activeReview, selectedFields);
    if (result?.success && result.matchedBeer) {
      setActiveReview((current) => mergeReviewRowWithMatchedBeer(current, result.matchedBeer));
    }
    return result;
  }, [activeReview, applySelectedChanges, reviewId]);

  const handleCompleteReview = useCallback(async () => {
    if (!activeReview) {
      return { success: false };
    }
    captureDuplicateReviewComplete(reviewId, activeReview.breweryId);
    const result = await completeReview(activeReview);
    if (result?.success) {
      await advanceAfterTerminalAction();
    }
    return result;
  }, [activeReview, advanceAfterTerminalAction, completeReview, reviewId]);

  const handleAddBeer = useCallback(async () => {
    if (!activeReview) {
      return { success: false };
    }
    captureDuplicateReviewAddBeer(reviewId, activeReview.breweryId);
    const result = await addBeerAndCompleteReview(activeReview);
    if (result?.success) {
      await advanceAfterTerminalAction();
    }
    return result;
  }, [activeReview, addBeerAndCompleteReview, advanceAfterTerminalAction, reviewId]);

  const handlePrevious = useCallback(() => {
    if (!neighbors.previousReviewId) {
      return;
    }
    navigateToReview(neighbors.previousReviewId);
  }, [navigateToReview, neighbors.previousReviewId]);

  const handleNext = useCallback(() => {
    if (!neighbors.nextReviewId) {
      return;
    }
    navigateToReview(neighbors.nextReviewId);
  }, [navigateToReview, neighbors.nextReviewId]);

  const handleRefresh = useCallback(async () => {
    await refresh();
  }, [refresh]);

  return {
    activeReview,
    loading,
    error,
    actionsLoading,
    actionsError,
    clearError,
    showQueueNav,
    neighbors,
    handleApplySelected,
    handleCompleteReview,
    handleAddBeer,
    handlePrevious,
    handleNext,
    handleRefresh,
  };
}
